// v77.71 — Public Contact page.
//
// Simple form (name, email, role, subject, message) that POSTs to
// `/api/contact` and is delivered to info@coparentconsole.com via Resend.
// Public-only: NOT mounted under /app, so it never appears inside the
// authenticated app shell.

import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import LegalFooterInfo from "@/components/LegalFooterInfo";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { ArrowLeft, Mail, MapPin, CheckCircle2, AlertCircle, Send } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "parent",
    subject: "",
    message: "",
    // Honeypot — bots fill every field; humans never see this.
    website: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const setField = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in your name, email and message.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        subject: form.subject.trim() || null,
        message: form.message.trim(),
        website: form.website,
      });
      setDone(true);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.message ||
        "We couldn't send your message. Please try again.";
      setError(typeof msg === "string" ? msg : "We couldn't send your message.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-white dark:bg-[#0E1416] text-[#1A2225] dark:text-[#E8EEF0]"
      data-testid="contact-page"
    >
      {/* HEADER */}
      <header className="sticky top-0 z-30 border-b border-[#E5EBEC] dark:border-[#1F2A2D] bg-white/85 dark:bg-[#0E1416]/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="contact-brand">
            <Logo size={32} />
            <span className="font-heading font-semibold text-base sm:text-lg tracking-tight text-[#1A2225] dark:text-[#F4F8F9]">
              CoParent Console
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] inline-flex items-center gap-1.5"
            data-testid="contact-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Home</span>
          </Link>
        </div>
      </header>

      {/* HERO + FORM */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[#E0F2F2]/60 via-white to-white dark:from-[#0F2F30]/70 dark:via-[#0E1416] dark:to-[#0E1416]"
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-16 sm:pb-20">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1F9FA4] dark:text-[#3FB8BC]">Get in touch</div>
          <h1 className="font-heading text-[2rem] leading-[1.15] sm:text-5xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
            Contact CoParent Console
          </h1>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#48595E] dark:text-[#B6C2C7] max-w-2xl">
            Whether you&apos;re a parent looking for a calmer way to co-parent, a
            professional working with families, or simply have a question — we
            read every message and reply personally.
          </p>

          {/* Quick-info row */}
          <div className="mt-7 grid sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-[#E5EBEC] dark:border-[#2A3539] bg-white dark:bg-[#161E21]">
              <Mail className="w-4 h-4 text-[#116B6E] dark:text-[#3FB8BC] flex-shrink-0" strokeWidth={1.7} />
              <span className="text-[#1A2225] dark:text-[#F4F8F9] font-medium">
                info@coparentconsole.com
              </span>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-[#E5EBEC] dark:border-[#2A3539] bg-white dark:bg-[#161E21]">
              <MapPin className="w-4 h-4 text-[#116B6E] dark:text-[#3FB8BC] flex-shrink-0" strokeWidth={1.7} />
              <span className="text-[#1A2225] dark:text-[#F4F8F9] font-medium">
                Based in Ireland
              </span>
            </div>
          </div>

          {/* SUCCESS state */}
          {done ? (
            <div
              className="mt-10 border border-[#A7D8DA] bg-[#E0F2F2] dark:bg-[#0F2F30] dark:border-[#1F9FA4]/40 rounded-xl p-6 sm:p-8 text-center"
              data-testid="contact-success"
            >
              <div className="w-12 h-12 mx-auto rounded-full bg-[#1F9FA4] text-white inline-flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7" strokeWidth={2} />
              </div>
              <h2 className="font-heading text-2xl font-semibold mt-4 text-[#0B4D50] dark:text-[#F4F8F9]">
                Message sent
              </h2>
              <p className="mt-2 text-sm text-[#0B4D50] dark:text-[#A8B6BB]">
                Thanks for reaching out. We&apos;ll get back to you at{" "}
                <span className="font-medium">{form.email}</span> as soon as we
                can — usually within one working day.
              </p>
              <div className="mt-6">
                <Link to="/">
                  <Button
                    variant="outline"
                    className="rounded-md border-[#A7D8DA] dark:border-[#2A3539]"
                    data-testid="contact-success-home"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to home
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="mt-10 border border-[#E5EBEC] dark:border-[#2A3539] bg-white dark:bg-[#161E21] rounded-xl p-6 sm:p-8 space-y-5"
              data-testid="contact-form"
            >
              {error && (
                <div className="flex items-start gap-3 p-3 rounded-md bg-[#FBEFED] dark:bg-[#3A1A18] border border-[#E8B8B2] dark:border-[#7A2A22] text-[#8C2A22] dark:text-[#FFB6AE] text-sm" data-testid="contact-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                  <span>{error}</span>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="contact-name" className="text-sm">Your name</Label>
                  <Input
                    id="contact-name"
                    value={form.name}
                    onChange={setField("name")}
                    placeholder="Jane Doe"
                    required
                    autoComplete="name"
                    data-testid="contact-input-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-email" className="text-sm">Email address</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                    data-testid="contact-input-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">I&apos;m getting in touch as…</Label>
                <RadioGroup
                  value={form.role}
                  onValueChange={(v) => setForm((s) => ({ ...s, role: v }))}
                  className="grid sm:grid-cols-2 gap-2"
                  data-testid="contact-role-group"
                >
                  {[
                    { v: "parent", label: "A parent" },
                    { v: "professional", label: "A professional (solicitor / mediator / counsellor / Tusla / etc.)" },
                    { v: "media", label: "Media or press" },
                    { v: "other", label: "Something else" },
                  ].map((opt) => (
                    <label
                      key={opt.v}
                      className={`flex items-start gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition-colors ${
                        form.role === opt.v
                          ? "border-[#1F9FA4] bg-[#E0F2F2] dark:bg-[#1F9FA4]/15"
                          : "border-[#E5EBEC] dark:border-[#2A3539] hover:bg-[#F4FAFA] dark:hover:bg-[#0F2F30]/30"
                      }`}
                    >
                      <RadioGroupItem
                        value={opt.v}
                        id={`role-${opt.v}`}
                        className="mt-0.5"
                        data-testid={`contact-role-${opt.v}`}
                      />
                      <span className="text-sm text-[#1A2225] dark:text-[#F4F8F9] leading-snug">{opt.label}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="contact-subject" className="text-sm">Subject (optional)</Label>
                <Input
                  id="contact-subject"
                  value={form.subject}
                  onChange={setField("subject")}
                  placeholder="What's this about?"
                  data-testid="contact-input-subject"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="contact-message" className="text-sm">Message</Label>
                <Textarea
                  id="contact-message"
                  value={form.message}
                  onChange={setField("message")}
                  placeholder="Tell us a little about what you need…"
                  rows={6}
                  required
                  data-testid="contact-input-message"
                />
              </div>

              {/* Honeypot — visually hidden, never focusable. Bots fill it; we drop those. */}
              <div aria-hidden="true" className="hidden">
                <Label htmlFor="contact-website">Website</Label>
                <Input
                  id="contact-website"
                  value={form.website}
                  onChange={setField("website")}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <p className="text-xs text-[#6F828A] dark:text-[#7A8A91] leading-relaxed">
                By sending this message you agree we may contact you back at the
                email address above. See our{" "}
                <Link to="/privacy" className="text-[#116B6E] dark:text-[#3FB8BC] hover:underline">Privacy Policy</Link>{" "}
                for how we handle your data.
              </p>

              <Button
                type="submit"
                disabled={busy}
                className="w-full sm:w-auto bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-11 px-6"
                data-testid="contact-submit"
              >
                <Send className="w-4 h-4 mr-2" strokeWidth={1.8} />
                {busy ? "Sending…" : "Send message"}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E5EBEC] dark:border-[#1F2A2D] bg-[#FAFCFC] dark:bg-[#0B1011]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo size={24} />
            <span className="font-heading font-semibold text-sm text-[#1A2225] dark:text-[#F4F8F9]">CoParent Console</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-[#6F828A] dark:text-[#7A8A91]">
            <Link to="/about" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">About</Link>
            <span aria-hidden="true">·</span>
            <Link to="/help" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Help</Link>
            <span aria-hidden="true">·</span>
            <Link to="/privacy" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Privacy</Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Terms</Link>
            <span aria-hidden="true">·</span>
            <Link to="/security" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Security</Link>
          </div>
        </div>
        <LegalFooterInfo />
      </footer>
    </div>
  );
}
